import { useRef, type Ref, type RefObject } from 'react';
import { Lock, WarningCircle } from '@phosphor-icons/react';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { REDUCED_MOTION_DIALOG_CLASS } from '../components/ui/modalScrim';
import { StatusPill } from '../components/common/StatusPill';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { shopifyCopy, shopifyCountsCopy } from '@discovr/features/copy';
import { reconcilePermissions } from './shopifyScopes';
import { ShopifyPermissionsList } from './ShopifyPermissionsList';

export interface ShopifyManagePanelStore {
  name: string;
  shopDomain: string;
  connectedAt?: string;
  /** IANA zone of the store; connectedAt is shown in it, else in the browser zone. */
  timeZone?: string;
  productCount?: number;
  currencyCode?: string;
  /** Raw comma-separated grant exactly as the API returned it, e.g.
   *  "write_discounts,read_orders". Undefined or empty when this store has no
   *  recorded grant — a permanent state until it reconnects, not a transient
   *  read failure, so the panel says so rather than inviting a pointless retry. */
  grantedScopes?: string;
  /** The grant hasn't been read yet, as for a moment after connecting. */
  scopesLoading?: boolean;
  /** The scope list the backend asks for (`requested_scopes` on the status
   *  response). Drives which permissions the panel lists, so a scope the
   *  backend starts requesting appears without a frontend release. */
  requestedScopes?: string;
}

interface ShopifyManagePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  store: ShopifyManagePanelStore;
  /** Uninstall is brand_owner-only; the BE also enforces this. */
  isOwner: boolean;
  /** Opens the uninstall confirmation flow. */
  onUninstall: () => void;
  /** Lets the uninstall dialog hand focus back to the button that opened it. */
  uninstallButtonRef?: Ref<HTMLButtonElement>;
  /** The control that opened this panel. Without a `DialogTrigger` Radix has
   *  no trigger to restore focus to, so Escape/X dropped focus to `<body>`. */
  returnFocusTo?: RefObject<HTMLElement | null>;
}

function fmtDate(iso?: string, timeZone?: string): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  const opts: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZoneName: 'short',
  };
  try {
    return new Intl.DateTimeFormat(undefined, { ...opts, timeZone }).format(d);
  } catch {
    return new Intl.DateTimeFormat(undefined, opts).format(d);
  }
}

/** Permissions Discovr requested, each marked granted or not.
 *
 *  Shows the full requested set rather than only what Shopify echoed back, so
 *  a missing permission is visible as a gap instead of an absent row. An
 *  unknown grant renders as unknown — never as "0 scopes granted", which made
 *  a failed status call look identical to a store that approved nothing. */
function ShopifyPermissionsSection({
  grantedScopes,
  requestedScopes,
  loading,
}: {
  grantedScopes?: string;
  requestedScopes?: string;
  loading?: boolean;
}) {
  const rows = reconcilePermissions(grantedScopes, requestedScopes);

  if (rows === null) {
    return (
      <div className="space-y-2">
        <p className="text-muted-foreground text-caption-regular" id="shopify-permissions-label">
          Permissions
        </p>
        {loading ? (
          <p className="text-muted-foreground text-caption-regular" role="status">
            Checking permissions…
          </p>
        ) : (
          <Alert role={undefined} className="text-muted-foreground">
            <WarningCircle aria-hidden="true" />
            <span>No permissions are recorded for this store. Reconnect it to refresh them.</span>
          </Alert>
        )}
      </div>
    );
  }

  const requested = rows.filter((r) => r.requested);
  const extra = rows.filter((r) => !r.requested);
  const grantedCount = requested.filter((r) => r.granted).length;

  return (
    <div className="space-y-2">
      <p className="text-muted-foreground text-caption-regular" id="shopify-permissions-label">
        {shopifyCountsCopy.permissionsHeading(grantedCount, requested.length)}
      </p>
      {/* Same component the card's permissions dialog and the claim dialog
          use, so the wording a merchant approves is the wording they audit
          here. `granted` is what this surface adds: it exists only once a
          store is connected. */}
      <ShopifyPermissionsList rows={requested} labelId="shopify-permissions-label" />
      {extra.length > 0 && (
        <>
          <p className="text-muted-foreground text-caption-regular" id="shopify-permissions-extra-label">
            {shopifyCountsCopy.alsoGrantedHeading(extra.length)}
          </p>
          <p className="text-muted-foreground text-caption-regular" id="shopify-permissions-extra-note">
            {shopifyCountsCopy.alsoGrantedNote}
          </p>
          <ShopifyPermissionsList
            rows={extra.map((r) =>
              r.scope === 'read_pixels' && !r.purpose
                ? { ...r, purpose: shopifyCountsCopy.readPixelsPurpose }
                : r,
            )}
            labelId="shopify-permissions-extra-label"
            descriptionId="shopify-permissions-extra-note"
          />
        </>
      )}
    </div>
  );
}

/**
 * "Connection settings" panel for the connected Shopify integration (Figma node
 * 4609-24507): connected-on / products / currency summary row, a permissions
 * list with each scope's READ/WRITE tag, and an Uninstall action. Read-only
 * for non-owners — the same "Owner-only" gate the integration card already
 * enforces, mirrored here so opening the panel never dead-ends into a
 * control a brand_member can't use (Shopify App Store req 2.1.1).
 */
export function ShopifyManagePanel({
  open,
  onOpenChange,
  store,
  isOwner,
  onUninstall,
  uninstallButtonRef,
  returnFocusTo,
}: ShopifyManagePanelProps) {
  // Radix autofocuses the first tabbable element inside DialogContent by
  // default, which would otherwise land keyboard/AT focus directly on the
  // destructive Uninstall button — redirect initial focus to the (inert)
  // title instead, matching ManageIntegrationModal's non-destructive-first
  // tab order.
  const titleRef = useRef<HTMLHeadingElement>(null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-clarity-mask="True"
        className={`flex max-h-[90dvh] flex-col sm:max-w-md ${REDUCED_MOTION_DIALOG_CLASS}`}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          titleRef.current?.focus();
        }}
        onCloseAutoFocus={(e) => {
          if (!returnFocusTo?.current?.isConnected) return;
          e.preventDefault();
          returnFocusTo.current.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle ref={titleRef} tabIndex={-1}>
            {shopifyCopy.integrationCard.connectionSettings}
          </DialogTitle>
          <DialogDescription>
            Store connection details and permissions for this Shopify integration.
          </DialogDescription>
        </DialogHeader>

        <div data-slot="manage-body" className="-mx-2 min-h-0 flex-1 space-y-4 overflow-y-auto px-2 py-1">
          <div className="border-border bg-card flex items-center justify-between gap-2 rounded-lg border p-3">
            <div>
              <div className="flex items-center gap-1.5">
                <h3 className="text-foreground text-body-sm-medium">{store.name}</h3>
                <StatusPill status="active" />
              </div>
              <p className="text-muted-foreground text-caption-regular">{store.shopDomain}</p>
            </div>
          </div>

          <div className="divide-border border-border flex items-center divide-x rounded-md border">
            <div className="flex-1 space-y-0.5 px-3 py-2">
              <p className="text-muted-foreground text-caption-regular uppercase">Connected on</p>
              <p className="text-foreground text-body-sm-medium">{fmtDate(store.connectedAt, store.timeZone)}</p>
            </div>
            <div className="flex-1 space-y-0.5 px-3 py-2">
              <p className="text-muted-foreground text-caption-regular uppercase">Products</p>
              <p className="text-foreground text-body-sm-medium">
                {store.productCount?.toLocaleString() ?? '-'}
              </p>
            </div>
            <div className="flex-1 space-y-0.5 px-3 py-2">
              <p className="text-muted-foreground text-caption-regular uppercase">Currency</p>
              <p className="text-foreground text-body-sm-medium">{store.currencyCode ?? '-'}</p>
            </div>
          </div>

          <p className="text-muted-foreground text-caption-regular">{shopifyCopy.report.attribution}</p>

          <ShopifyPermissionsSection
            grantedScopes={store.grantedScopes}
            loading={store.scopesLoading}
            requestedScopes={store.requestedScopes}
          />

          {isOwner ? (
            <DialogFooter className="flex-col gap-2 sm:flex-col">
              <div className="flex items-center gap-2" data-slot="danger-zone">
                <hr className="border-border flex-1" />
                <span className="text-muted-foreground text-caption-regular">{shopifyCountsCopy.dangerZone}</span>
                <hr className="border-border flex-1" />
              </div>
              <Button
                ref={uninstallButtonRef}
                variant="destructive"
                className="w-full justify-center"
                onClick={onUninstall}
              >
                {shopifyCopy.uninstall.action}
              </Button>
            </DialogFooter>
          ) : (
            <p className="text-muted-foreground flex items-center justify-center gap-1.5 text-caption-regular">
              <Lock className="size-3.5" /> {shopifyCopy.uninstall.ownerOnly}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
